import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Permission } from "@shared/contracts/permissions";

export interface RoleRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  audience: "internal" | "client" | "all";
  isSystem: boolean;
  permissions: Permission[];
}

export interface PermissionGroup {
  key: string;
  label: string;
  permissions: Array<{ key: Permission; label: string }>;
}

export const roleEditorSchema = z.object({
  name: z.string().min(1, "Role name is required"),
  description: z.string().optional(),
  audience: z.enum(["internal", "client", "all"]),
  permissions: z.array(z.string()).min(1, "Select at least one permission"),
});

export type RoleEditorValues = z.infer<typeof roleEditorSchema>;

interface RoleEditorProps {
  groups: PermissionGroup[];
  defaultValues?: Partial<RoleEditorValues>;
  audienceDisabled?: boolean;
  isSystemRole?: boolean;
  roleSlug?: string | null;
  isPending?: boolean;
  submitLabel?: string;
  onSubmit: (data: RoleEditorValues) => void;
}

export function RoleEditor({
  groups,
  defaultValues,
  audienceDisabled,
  isSystemRole,
  roleSlug,
  isPending,
  submitLabel = "Save role",
  onSubmit,
}: RoleEditorProps) {
  const [permissionSearch, setPermissionSearch] = useState("");
  const form = useForm<RoleEditorValues>({
    resolver: zodResolver(roleEditorSchema),
    defaultValues: {
      name: "",
      description: "",
      audience: "internal",
      permissions: [],
      ...defaultValues,
    },
  });

  useEffect(() => {
    form.reset({
      name: "",
      description: "",
      audience: "internal",
      permissions: [],
      ...defaultValues,
    });
  }, [defaultValues, form]);

  const selectedPermissions = form.watch("permissions");
  const filteredGroups = useMemo(() => {
    const term = permissionSearch.trim().toLowerCase();
    if (!term) return groups;

    return groups
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((permission) => {
          const haystack = `${permission.label} ${permission.key}`.toLowerCase();
          return haystack.includes(term);
        }),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [groups, permissionSearch]);

  const permissionCount = selectedPermissions.includes("*")
    ? "Full access"
    : `${selectedPermissions.length} selected`;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Role Details</CardTitle>
              <CardDescription>
                Define who this role is for and what access it should grant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="audience"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Audience</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={audienceDisabled}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="internal">Internal</SelectItem>
                          <SelectItem value="client">Client</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value ?? ""}
                        rows={4}
                        placeholder="Describe the responsibilities and intended usage for this role."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="gap-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>Permissions</CardTitle>
                  <CardDescription>
                    Search and assign the capabilities this role should bundle.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{permissionCount}</Badge>
                  <Input
                    value={permissionSearch}
                    onChange={(event) => setPermissionSearch(event.target.value)}
                    placeholder="Search permissions"
                    className="w-full md:w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {filteredGroups.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                  No permissions match the current search.
                </div>
              ) : (
                filteredGroups.map((group) => (
                  <div key={group.key} className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold">{group.label}</p>
                      <p className="text-xs text-muted-foreground">{group.permissions.length} options</p>
                    </div>
                    <div className="grid gap-2 lg:grid-cols-2">
                      {group.permissions.map((permission) => (
                        <label
                          key={permission.key}
                          className="flex items-center justify-between gap-3 rounded-md border px-3 py-3"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{permission.label}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {permission.key}
                            </p>
                          </div>
                          <Checkbox
                            checked={selectedPermissions.includes(permission.key)}
                            onCheckedChange={(checked) => {
                              const next = new Set(form.getValues("permissions"));
                              if (checked === true) next.add(permission.key);
                              else next.delete(permission.key);
                              form.setValue("permissions", [...next], {
                                shouldValidate: true,
                              });
                            }}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
              <FormField
                control={form.control}
                name="permissions"
                render={() => <FormMessage />}
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
              <CardDescription>
                Review the shape of this role before saving.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {roleSlug ? (
                <div>
                  <p className="text-muted-foreground">Slug</p>
                  <p className="font-medium">{roleSlug}</p>
                </div>
              ) : null}
              <div>
                <p className="text-muted-foreground">Audience</p>
                <p className="font-medium capitalize">{form.watch("audience")}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Permission count</p>
                <p className="font-medium">{permissionCount}</p>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {isSystemRole ? <Badge>System role</Badge> : <Badge variant="secondary">Custom role</Badge>}
                {audienceDisabled ? <Badge variant="outline">Audience locked</Badge> : null}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2">
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving..." : submitLabel}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
